import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { API_URL } from "../../../utils/constant";
import AccountingForm from "./accountingform";
import { Pen, Trash } from "lucide-react";
import { customTableStyles } from "../../../utils/tableStylings";

const Accounting = () => {
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const company_id = localStorage.getItem("companyID");
      const res = await axios.get(
        `${API_URL}/accounting/accounting?company_id=${company_id}`
      );
      setAccounts(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const handleAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (account) => {
    setEditData(account);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the accounting account!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/accounting/accounting/${id}`);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The Account has been deleted.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
      fetchAccounts();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete the account.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
          toast.addEventListener("mouseenter", Swal.stopTimer);
          toast.addEventListener("mouseleave", Swal.resumeTimer);
        },
      });
    }
  };

  // DataTable Columns
  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Identification",
      selector: (row) => row.identification,
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
    },
    {
      name: "Account No",
      selector: (row) => row.account_no,
    },
    {
      name: "IFSC Code",
      selector: (row) => row.ifsc_code,
    },
    {
      name: "Classification",
      selector: (row) => row.classification,
    },
    {
      name: "Subledger",
      selector: (row) => (row.is_subledger ? "Yes" : "No"),
    },
    {
      name: "Loan Account",
      selector: (row) => (row.is_loan_account ? "Yes" : "No"),
    },
    {
      name: "TDS Applicable",
      selector: (row) => (row.tds_applicable ? "Yes" : "No"),
    },
    {
      name: "PAN",
      selector: (row) => row.pan,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button
            className="btn btn-sm btn-warning me-2"
            onClick={() => handleEdit(row)}
          >
            <Pen size={16} />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => handleDelete(row.id)}
          >
            <Trash size={16} />
          </button>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <>
      <div className="row" style={{ marginTop: "60px" }}>
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="text-primary mb-0">Accounting Accounts</h3>
              <button className="btn btn-primary" onClick={handleAdd}>
                + Add Account
              </button>
            </div>

            <div className="card-body">
              <DataTable
                columns={columns}
                data={accounts}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                customStyles={customTableStyles}
              />
            </div>

            {showModal && (
              <AccountingForm
                show={showModal}
                heading={editData ? "Edit Account" : "Add Account"}
                initialData={editData}
                onClose={() => setShowModal(false)}
                onSuccess={fetchAccounts}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Accounting;
